import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContextType, LoginCredentials, RegisterCredentials, User } from '../types/auth.types';
import { authService } from '../services/auth.service';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          const user = await authService.validateToken();
          setUser(user);
        } catch (error) {
          console.error('Token validation failed:', error);
          authService.clearToken();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const { access_token } = await authService.login(credentials);
    authService.setToken(access_token);
    
    // After setting token, fetch user data
    try {
      const user = await authService.validateToken();
      setUser(user);
      navigate('/home');
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      authService.clearToken();
      throw error;
    }
  };

  const logout = () => {
    authService.clearToken();
    setUser(null);
    navigate('/auth');
  };

  const register = async (credentials: RegisterCredentials) => {
    if (credentials.password !== credentials.confirmPassword) {
      throw new Error('Passwords do not match');
    }
    
    const { access_token } = await authService.register(credentials);
    authService.setToken(access_token);
    
    try {
      const user = await authService.validateToken();
      setUser(user);
      navigate('/home');
    } catch (error) {
      console.error('Failed to fetch user data after registration:', error);
      authService.clearToken();
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
